/**
 * Header component with user info and navigation.
 * 
 * @module components/layout/Header
 */

import { Menu, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getInitials } from '../../lib/utils';

interface HeaderProps {
  onMenuClick: () => void;
}

/**
 * Header component with user info and logout.
 * 
 * @param props - Header props
 * @returns Header element
 */
export function Header({ onMenuClick }: HeaderProps) {
  const { adminUser, signOut } = useAuth();

  return (
    <header className="fixed top-0 left-0 right-0 lg:left-64 z-40 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6">
      {/* Left: Menu button (mobile) */}
      <button
        onClick={onMenuClick}
        className="p-2 text-gray-400 hover:text-gray-600 lg:hidden"
        aria-label="Open menu"
      >
        <Menu className="h-6 w-6" />
      </button>

      {/* Center: Page title (optional, can be used with breadcrumbs) */}
      <div className="hidden lg:block" />

      {/* Right: User info and logout */}
      <div className="flex items-center gap-4">
        {/* User info */}
        {adminUser && (
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-sm font-medium text-primary-700">
                {getInitials(adminUser.full_name)}
              </span>
            </div>
            {/* Name and role */}
            <div className="hidden sm:block">
              <p className="text-sm font-medium text-gray-900">{adminUser.full_name}</p>
              <p className="text-xs text-gray-500 capitalize">{adminUser.role.replace('_', ' ')}</p>
            </div>
          </div>
        )}

        {/* Logout button */}
        <button
          onClick={signOut}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Sign out"
        >
          <LogOut className="h-4 w-4" />
          <span className="hidden sm:inline">Sign out</span>
        </button>
      </div>
    </header>
  );
}
